import { useCallback, useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Tab, Tabs } from '@ds';
import { MarkerRouteIcon } from '@icons';
import { AuthContext } from '@lp/core/auth';
import { FilterDropdown, InfoBanner } from '@ui';
import { CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP } from '@lp/features/RoutePlanner/config';
import { useDonorEta } from '@lp/features/RoutePlanner/hooks/useDonorEta';
import { useRouteDetails } from '@lp/features/RoutePlanner/hooks/useRouteDetails';
import { useRouteLocations } from '@lp/features/RoutePlanner/hooks/useRouteLocations';
import { Case, CaseFacility, CaseStatus, RoutePlanStatus } from '@/api/apis/case';
import { RouteTypeEnum } from '@/api/apis/route';
import { quoteStatusNames } from '@/constants/case/case.constants';
import { RouteBuilderPermissions } from '@/permissions';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { convertToTimezoneOffset, getTimeZonedDateString, isAllowedStatus } from '@/utils/helpers';
import { RouteFilterType } from '@/types/route';
import { CaseLogisticsMap } from './CaseLogisticsMap';
import { CaseLogisticsRouteList } from './CaseLogisticsRouteList';
import { CaseLogisticsRouteSchema } from './CaseLogisticRouteSchema';
import { getRouteFilterTypeList } from '../utils';

export type CaseDetailsLogisticsProps = {
  caseId: string;
  donorHospital: CaseFacility;
  customFilterTitles: Partial<Record<RouteFilterType, string>>;
  showDryRunRoute?: boolean;
};

enum LegListTabs {
  main = 'main',
  surgeon = 'surgeon',
  dryRun = 'dryRun',
}

export const CaseDetailsLogistics = ({
  caseId,
  donorHospital,
  customFilterTitles,
  showDryRunRoute = false,
}: CaseDetailsLogisticsProps) => {
  const { checkPermission } = useContext(AuthContext);
  const {
    txCenterDetails: txCenter,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    organType,
    routePlanningStatus,
    status,
    quote,
  } = useCaseDetails() as Case;

  const mainRoute = useRouteDetails(RouteTypeEnum.Main);
  const dryRunRoute = useRouteDetails(RouteTypeEnum.DryRun);
  const { list } = useRouteLocations();
  const [filterType, setFilterType] = useState<RouteFilterType>(RouteFilterType.Full);

  const getFilterTitle = useCallback(
    (type: RouteFilterType) => customFilterTitles?.[type] || CASE_LOGISTICS_ROUTE_FILTER_TYPE_TITLE_MAP[type],
    [customFilterTitles],
  );

  const routeFilterTypeList = useMemo(() => getRouteFilterTypeList(getFilterTitle), [getFilterTitle]);

  const hasUpdateLogisticsPermission = checkPermission(RouteBuilderPermissions.ViewBuilder);

  const donorHospitalDateTimeString = useMemo(() => {
    return getTimeZonedDateString(donorHospitalDate, donorHospitalTime, donorHospitalTimeZone);
  }, [donorHospitalDate, donorHospitalTime, donorHospitalTimeZone]);

  const recipientHospitalDateTimeString = useMemo(() => {
    return getTimeZonedDateString(recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone);
  }, [recipientHospitalDate, recipientHospitalTime, recipientHospitalTimeZone]);

  const isRoutePlanningDone =
    routePlanningStatus &&
    [
      RoutePlanStatus.RoutePlanningDone,
      RoutePlanStatus.PendingResourcesBooking,
      RoutePlanStatus.ResourcesBooked,
    ].includes(routePlanningStatus);

  const isRouteDetailsAllowed = isAllowedStatus({
    statusFrom: CaseStatus.ResourcesBooked,
    statusTo: CaseStatus.Closed,
    currentStatus: status,
  });

  const isRouteDone = (status === CaseStatus.PendingLogistics && isRoutePlanningDone) || isRouteDetailsAllowed;
  const showRouteBuilderLinks =
    (isRouteDetailsAllowed || status === CaseStatus.PendingLogistics) && !isRoutePlanningDone;

  const { offsetInMs } = useDonorEta(mainRoute?.legs);

  const isEmptyRoute = !mainRoute.legs.length && !dryRunRoute.legs.length;

  return (
    <>
      <div className="w-full flex shrink-0 h-50 bg-base-60 rounded-2xl overflow-hidden z-10">
        <CaseLogisticsMap
          donorHospital={donorHospital}
          txCenter={txCenter}
          legs={mainRoute.legs}
          locations={list}
          organType={organType}
          isRouteDone={isRouteDone}
        />
      </div>

      {isRouteDone ? (
        <>
          <div className=" w-full flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-l-sb text-base-5 mr-0.5">Route Details</span>
              <CaseLogisticsRouteSchema />
            </div>
            <Button variant="solid" size="sm" isDisabled>
              {quote ? quoteStatusNames[quote.status] : 'No Quote'}
            </Button>
          </div>

          {!!offsetInMs && (
            <div className="w-full">
              <InfoBanner iconType="redWarning">
                {`The ETA to Donor Hospital is ${convertToTimezoneOffset(offsetInMs, false)}h after current OR time.`}
              </InfoBanner>
            </div>
          )}

          <div className="relative w-full overflow-y-scroll">
            <Tabs
              aria-label="Route Tabs"
              variant="underlined"
              classNames={{
                tabContent: 'text-m-cps',
                base: 'w-full',
                tabList: 'p-0 flex w-full overflow-x-auto',
                tab: 'basis-1/4',
                panel: 'w-full h-full',
              }}
            >
              <Tab key={LegListTabs.main} title="Main Route">
                <CaseLogisticsRouteList
                  filterType={filterType}
                  type={RouteTypeEnum.Main}
                  donorHospital={donorHospital}
                  txCenter={txCenter}
                  donorHospitalDateTimeString={donorHospitalDateTimeString}
                  recipientHospitalDateTimeString={recipientHospitalDateTimeString}
                  isRouteDone={isRouteDone}
                />
              </Tab>

              {showDryRunRoute && (
                <Tab key={LegListTabs.dryRun} title="Dry Run">
                  <CaseLogisticsRouteList
                    filterType={filterType}
                    type={RouteTypeEnum.DryRun}
                    donorHospital={donorHospital}
                    txCenter={txCenter}
                    donorHospitalDateTimeString={donorHospitalDateTimeString}
                    recipientHospitalDateTimeString={recipientHospitalDateTimeString}
                    isRouteDone={isRouteDone}
                  />
                </Tab>
              )}
            </Tabs>

            <div className="absolute right-1 top-1 flex flex-col gap-4 items-end">
              <FilterDropdown
                title={getFilterTitle(filterType)}
                items={routeFilterTypeList}
                selectedKeys={[filterType]}
                mode="single"
                size="sm"
                showCounter={false}
                disallowEmptySelection
                onSelectedKeysChange={(selected) => setFilterType(selected[0] as RouteFilterType)}
              />
            </div>
          </div>
        </>
      ) : (
        <div className="flex flex-col flex-1 gap-7 items-center w-full">
          <div className="flex flex-col gap-6 items-center justify-center mt-45">
            <MarkerRouteIcon className="text-base-55 size-32.5" />
            {showRouteBuilderLinks && isEmptyRoute && (
              <span className="text-l-r text-base-30">No route has been added</span>
            )}
          </div>

          {showRouteBuilderLinks && hasUpdateLogisticsPermission && (
            <Button as={Link} size="md" to={`/case/${caseId}/planner`} className="min-w-40">
              {isEmptyRoute ? 'New Route' : 'Edit Route'}
            </Button>
          )}
        </div>
      )}
    </>
  );
};
